<template>
  <div v-if="coPilotUrl !== null" class="product-variant-select">
    <label class="product-variant-select__label">{{
      $textDictionary['pdp.copilot.label'] ?? 'pdp.copilot.label'
    }}</label>
    <button
      v-if="accessoryAssistantStore.selectedProduct"
      class="d-flex justify-content-between align-items-center product-variant-select__fake-select-box"
      @click="removeFromAccessoryAssistantStore"
    >
      <div>
        {{ accessoryAssistantStore.selectedDecoratedProductPriceLabel }}
      </div>
      <Icon class="pe-2" name="close" size="small"> </Icon>
    </button>

    <button
      v-else
      class="d-flex justify-content-between align-items-center product-variant-select__fake-select-box"
      @click="openCopilotModal(store.decoratedProduct?.sku)"
    >
      <div>
        {{ $textDictionary['pdp.copilot.button'] ?? 'pdp.copilot.button' }}
      </div>
      <Icon class="pe-2" name="glass" scale-factor="1.2"> </Icon>
    </button>

    <ModalModule
      v-model="showModal"
      :title="
        $textDictionary['pdp.copilot.modal.headline'] ??
        'pdp.copilot.modal.headline'
      "
      close-icon-text="close"
      :esc-to-close="true"
      :click-to-close="true"
      :is-full-screen="false"
      @close="
        showModal = false;
        isLoading = true;
      "
      @abort="
        showModal = false;
        isLoading = true;
      "
    >
      <div id="webchat-container">
        <div v-if="isLoading" class="col-lg-12 col-sm-12">
          <loading-skeleton
            type="block"
            style="
              height: 66px;
              width: 460px;
              border-radius: 20px;
              padding: 8px 16px;
            "
          /><br />
          <spacer-block spacing-grid="xsmall"></spacer-block>
          <loading-skeleton
            type="button"
            class="me-3 mt-2"
            style="height: 46px; width: 180px"
          />
          <loading-skeleton
            type="button"
            class="me-3 mt-2"
            style="height: 46px; width: 140px"
          />
          <loading-skeleton
            type="button"
            class="me-3 mt-2"
            style="height: 46px; width: 310px"
          />
          <loading-skeleton
            type="button"
            class="me-3 mt-2"
            style="height: 46px; width: 315px"
          />
        </div>
        <div
          id="webchat"
          role="main"
          class="webchat rte-text d-flex justify-content-center col-lg-12 col-sm-12"
        ></div>
      </div>
      <div id="the-end" />
    </ModalModule>
  </div>
</template>

<script setup lang="ts">
// @ts-nocheck
import {
  Button,
  ModalModule,
  Icon,
  LoadingSkeleton,
  SpacerBlock
} from '@hypercodestudio/basler-components';
import { useProductDetailPageStore } from '~/stores/productDetailPageStore/productDetailPageStore';
import { useAccessoryAssistantStore } from '~/stores/productDetailPageStore/accessoryAssistantStore';

const { $textDictionary, $shopStore, $analytics, $locale } = useNuxtApp();
const store = useProductDetailPageStore();
const accessoryAssistantStore = useAccessoryAssistantStore();
const showModal = ref(false);
const isLoading = ref(true);
const coPilotUrl = ref();
const accessoryAssistantSelectedProducts = ref();

onMounted(() => {
  const webChatScript = document.createElement('script');
  webChatScript.setAttribute(
    'src',
    'https://cdn.botframework.com/botframework-webchat/latest/webchat.js'
  );
  document.head.appendChild(webChatScript);
});

async function openCopilotModal(sku: string) {
  showModal.value = true;
  const tokenEndpointURL = new URL(
    'https://default744babbe24234ffca968eb9943b743.86.environment.api.powerplatform.com/powervirtualagents/botsbyschema/cr8a2_bruce/directline/token?api-version=2022-03-01-preview'
  );

  const apiVersion =
    tokenEndpointURL.searchParams.get('api-version') || '2022-03-01';
  const locale = $locale.value || 'en';

  const [directLineURL, token] = await Promise.all([
    fetch(
      new URL(
        `/powervirtualagents/regionalchannelsettings?api-version=${apiVersion}`,
        tokenEndpointURL
      )
    )
      .then((r) =>
        r.ok ? r.json() : Promise.reject('Fehler beim Channel-URL Abrufen')
      )
      .then(({ channelUrlsById: { directline } }) => directline),
    fetch(tokenEndpointURL)
      .then((r) =>
        r.ok ? r.json() : Promise.reject('Fehler beim Token-Abrufen')
      )
      .then(({ token }) => token)
  ]);

  const store = window.WebChat.createStore({}, () => (next) => (action) => {
    if (action.type === 'DIRECT_LINE/INCOMING_ACTIVITY') {
      const event = new Event('webchatincomingactivity');

      event.data = action.payload.activity;
      window.dispatchEvent(event);
    }

    return next(action);
  });

  const styleOptions = {
    hideUploadButton: true,
    backgroundColor: '#f4f6f8',
    bubbleBackground: '#ffffff',
    bubbleTextColor: '#000000',
    bubbleBorderRadius: 14,
    bubbleFromUserBackground: '#f59d21',
    bubbleFromUserTextColor: '#ffffff',
    bubbleFromUserBorderRadius: 14,
    fontSizeSmall: '90%',
    primaryFont: "'Inter var', sans-serif",
    sendBoxBackground: '#ffffff',
    sendBoxTextColor: '#000000',
    sendBoxButtonColor: '#00457c',
    sendBoxButtonColorOnHover: '#1a2c52',
    suggestedActionLayout: 'flow'
  };

  const styleSet = window.WebChat.createStyleSet({
    bubbleBackground: '#ffffff',
    bubbleTextColor: '#000000',
    bubbleBorderRadius: 16,
    bubbleBorder: '1px solid #e0e0e0',
    bubbleBoxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    bubbleFromUserBackground: '#f59d21',
    bubbleFromUserTextColor: '#ffffff',
    bubbleFromUserBorderRadius: 16,
    bubbleFromUserBoxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
    primaryFont: "'Inter var', sans-serif",
    suggestedActionBackground: 'white',
    suggestedActionTextColor: '#000000',
    suggestedActionBorder: '1px solid #00457c',
    suggestedActionBorderRadius: 14,
    suggestedActionPadding: 6
  });

  // Button-Styling
  styleSet.suggestedAction = {
    ...styleSet.suggestedAction,
    margin: '0.1px'
  };

  const directLine = window.WebChat.createDirectLine({
    domain: new URL('v3/directline', directLineURL),
    token
  });

  window.WebChat.renderWebChat(
    {
      directLine,
      store,
      locale,
      styleOptions,
      styleSet
    },
    document.getElementById('webchat')
  );

  window.addEventListener('webchatincomingactivity', eventData);

  const subscription = directLine.connectionStatus$.subscribe({
    next(value: number) {
      if (value === 2) {
        directLine
          .postActivity({
            type: 'event',
            name: 'startConversation',
            locale,
            value: {
              sku: sku,
              userRegion: $shopStore.value,
              currentURL: window.location.href,
              language: locale
            }
          })
          .subscribe();
        subscription.unsubscribe();
      }
    }
  });
}

watch(
  store.currentVariant,
  () => {
    removeFromAccessoryAssistantStore();
  },
  { immediate: true }
);

function removeFromAccessoryAssistantStore() {
  accessoryAssistantStore.removeSelectedDecoratedProduct();
}

async function addToAccessoryAssistantStore(sku: string) {
  await getProduct(sku);

  accessoryAssistantStore.setSelectedDecoratedProduct(
    accessoryAssistantSelectedProducts.value[0]
  );
  window.removeEventListener('webchatincomingactivity', eventData);
}

async function getProduct(sku: string) {
  const response = await $fetch<ProductsBySkusResponseModel>(
    '/api/magento/productsBySkus',
    {
      retry: 0,
      // we remove a timeout here as magento might need some time to respond
      query: {
        store: $shopStore.value,
        locale: $locale.value,
        pageSize: 1000,
        skus: sku
      }
    }
  );
  if (response.items) {
    accessoryAssistantSelectedProducts.value = await Promise.all(
      response.items.map(async (item: ProductsByFilterItem) => {
        return item;
      })
    );
  }
}

function eventData(event: MessageEvent) {
  setTimeout(function () {
    const container = document.getElementById('the-end');
    if (container) {
      container.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, 250);

  if (
    event.data.type === 'message' &&
    event.data.inputHint &&
    event.data.inputHint === 'acceptingInput'
  ) {
    isLoading.value = false;
  }
  if (
    event.data.type === 'event' &&
    event.data.value &&
    event.data.name &&
    event.data.name === 'copilot_userSelectsAccessory' &&
    !isEmpty(event.data.value) &&
    !isEmpty(event.data.value.sku)
  ) {
    addToAccessoryAssistantStore(event.data.value.sku);
    showModal.value = false;
  }
  if (
    event.data.type === 'event' &&
    event.data.value &&
    event.data.name &&
    (event.data.name === 'copilot_showAccessoryDetails' ||
      event.data.name === 'copilot_showMoreProducts') &&
    !isEmpty(event.data.value) &&
    !isEmpty(event.data.value.url)
  ) {
    window.open(event.data.value.url, '_blank');
    showModal.value = false;
  }
  if (
    event.data.type === 'event' &&
    !isEmpty(event.data.value) &&
    !isEmpty(event.data.name)
  ) {
    const eventName = event.data.value.accessory_type
      ? event.data.name + '_' + event.data.name
      : event.data.name;
    trackEvents(eventName, event.data.value);
  }
  /*if (
    event.data.type === 'event' &&
    event.data.name === 'copilot_scrollTo' &&
    !isEmpty(event.data.value) &&
    !isEmpty(event.data.value.id)
  ) {
    setTimeout(function () {
      const container = document.getElementById('the-end');
      if (container) {
        container.scrollIntoView({ behavior: 'smooth', block: 'start' });
        const visitedId = container.getAttribute('id');
        if (visitedId === 'copilot_AdaptiveCard') {
          container.setAttribute('id', visitedId + '-visited');
        }
      }
    }, 250);
  }*/
}

function trackEvents(action: string, label: string[]) {
  $analytics.pushToDataLayer({
    action: action,
    category: 'CoPilotAccessoryAssistant',
    event: 'click_Internal',
    label: JSON.stringify(label)
  });
}
</script>

<style>
.product-variant-select__fake-select-box {
  padding: calc(11px - var(--variant-border-width, 1px))
    calc(13px - var(--variant-border-width, 1px));
  border: var(--variant-border-width, 3px) solid var(--variant-border-color);
  border-radius: 12px;
  outline: 4px solid var(--variant-outline-color);
  outline-offset: 0;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1), 0 7px 7px 0 rgba(0, 0, 0, 0.09),
    0 15px 9px 0 rgba(0, 0, 0, 0.05), 0 27px 11px 0 rgba(0, 0, 0, 0.01);
  transition-property: outline-color, border, box-shadow;
  transition-duration: 0.2s;
  transition-timing-function: ease-in-out;
  padding-block: calc(11px - var(--variant-border-width, 1px));
  padding-inline: calc(13px - var(--variant-border-width, 1px))
    calc(13px - var(--variant-border-width, 1px));
  background: var(--bslr-body-bg) no-repeat calc(100% - 16px) 50%;
  background-size: 16px 9px;
  font-size: var(--base-font-size);
  font-weight: 600;
  line-height: 26px;
  text-align: left;
  color: var(--variant-text-color, #222);
  cursor: var(--variant-cursor, default);
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  display: block;
  width: 100%;
  height: 52px;
}
.product-variant-select__fake-select-box:hover {
  --variant-outline-color: var(--bslr-color-element-interactive-inactive);
}
#webchat-container {
  min-height: 500px;
  min-width: 550px;
}
@media (max-width: 768px) {
  #webchat-container {
    max-width: 100% !important;
  }
}
#webchat {
  flex: 1;
}
/* ------------------------ */
/* ✉️ Send Box deaktivieren */
/* ------------------------ */

#webchat .webchat__send-box__main {
  display: none !important;
}
#webchat .webchat__suggested-actions__button {
  border-width: 2px;
  border-color: transparent;
  padding-block: 8px;
  padding-inline: 20px;
  background-color: rgb(var(--bslr-color-dark-alternate-rgb) / 10%);
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: var(--button-border-width) solid var(--button-border-color);
  border-radius: 40px;
  color: var(--button-text-color);
  font-weight: 600;
  font-family: 'Inter var', -apple-system, blinkmacsystemfont, 'Segoe UI',
    roboto, oxygen, ubuntu, helvetica, arial, sans-serif, 'Apple Color Emoji',
    'Segoe UI Emoji', 'Segoe UI Symbol';
  line-height: 1.3;
  text-decoration: none;
  transition-property: background, border-color, box-shadow, opacity, color;
  transition-duration: 0.2s;
  transition-timing-function: ease-in-out;
  cursor: pointer;
}
#webchat .webchat__suggested-actions__button:hover {
  background-color: rgb(var(--bslr-color-dark-alternate-rgb) / 20%);
}
#webchat .webchat__bubble .webchat__bubble__content {
  padding: 16px 0;
  border: none;
}
#webchat .webchat__bubble--from-user .webchat__bubble__content {
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  border: 2px solid white;
  border-radius: 40px;
  padding: 8px 16px;
  max-width: var(--max-width, 290px);
  font-size: var(--tag-pill-font-size-medium);
  color: var(--bslr-tag-list-item-color);
  text-decoration: none;
  background-color: var(--bslr-tag-list-item-bg-color);
  transition: background-color 0.3s ease-in-out;
}
#webchat
  .webchat__bubble--from-user
  .webchat__bubble__content
  .webchat__text-content,
#webchat .webchat__text-content {
  padding: 0;
}
#webchat .ac-input.ac-textInput,
#webchat .ac-input.ac-numberInput {
  font-family: inherit;
  font-feature-settings: inherit;
  font-size: 1rem;
  color: #334155;
  background: #fff;
  padding-block: 0.5rem;
  padding-inline: 0.625rem;
  border: 1px solid #000;
  transition: background 0.2s, color 0.2s, border-color 0.2s, outline-color 0.2s;
  appearance: none;
  border-radius: 6px;
  outline-color: transparent;
}
#webchat .ac-input.ac-textInput:focus,
#webchat .ac-input.ac-numberInput:focus {
  border-color: var(--bslr-color-highlight);
  box-shadow: 0 0 0 0.1rem var(--bslr-color-highlight);
  outline: 2px none var(--bslr-color-highlight);
  outline-offset: 1px;
}
#webchat #copilot_submitUserSpecInput button,
#webchat #copilot_userSelectsAccessory button {
  background: none;
  color: var(--bslr-secondary);
  border: 2px solid var(--bslr-secondary);
  border-radius: 40px;
  padding: 10px 20px;
  font-weight: 600;
  transition: background-color 0.3s ease, color 0.3s ease;
}
#webchat #copilot_userSelectsAccessory button {
  margin-top: 1rem;
}

#webchat #copilot_submitUserSpecInput button:hover,
#webchat #copilot_userSelectsAccessory button:hover {
  background-color: rgb(245 157 33 / 15%);
}
#webchat #copilot_showAccessoryDetails button,
#webchat #copilot_showMoreProducts button {
  background: none;
  border: none;
  font-size: small;
  color: var(--bslr-color-highlight);
}
#webchat #copilot_showAccessoryDetails button:hover,
#webchat #copilot_showMoreProducts button:hover {
  color: var(--bslr-color-link-hover);
}
#webchat #copilot_showAccessoryDetails button {
  font-size: small;
  font-weight: 400;
  padding: 0 !important;
  justify-content: start !important;
}
#webchat #copilot_showMoreProducts button {
  margin-top: 0.5rem;
  text-decoration: underline;
}
#webchat .webchat__adaptive-card-renderer:has(#copilot_adaptiveProductCard) {
  display: flex;
  justify-content: center;
}
#webchat #copilot_adaptiveProductCard {
  border-radius: 16px;
  box-shadow: 0 0 20px #00000026 !important;
  overflow: hidden;
  position: relative;
  z-index: 1;
  margin: 0 auto;
  --columns: 2;
  width: 324px;
}
#webchat #copilot_adaptiveProductCard .ac-columnSet .ac-container:first-child {
  width: 50%;
}
#webchat
  #copilot_adaptiveProductCard
  .ac-columnSet
  .ac-container:first-child
  .ac-textBlock
  p {
  color: var(--bslr-color-lucent-text) !important;
  font-size: var(--base-font-size);
  font-weight: var(--font-weight-body);
  padding-inline-end: 6px;
  line-height: 1.3;
  padding: 4px 0;
  text-align: left;
  vertical-align: top;
}
#webchat #copilot_adaptiveProductCard .ac-vertical-separator {
  display: none !important;
}
#webchat #copilot_adaptiveProductCard #copilot_userSelectsAccessory button {
  padding: 6px 20px;
}
#webchat
  #copilot_adaptiveProductCard
  .ac-container:first-child
  .ac-textBlock
  p {
  font-size: var(--base-font-size);
  padding-top: 8px;
}
#webchat #copilot_adaptiveProductCard img.ac-image {
  width: 75% !important;
}
#webchat
  #copilot_adaptiveProductCard
  .ac-columnSet
  .ac-container:last-child
  .ac-textBlock
  p {
  color: var(--bslr-color-lucent-text-strong) !important;
  font-size: var(--base-font-size);
  font-weight: var(--font-weight-body);
  padding-inline-end: 6px;
  line-height: 1.3;
  padding: 4px 0;
  text-align: left;
  vertical-align: top;
}
#webchat
  #copilot_adaptiveProductCard
  .ac-columnSet
  .ac-container:last-child
  .ac-textBlock:last-child
  p {
  font-weight: bold;
}
#webchat #copilot_AdaptiveNoLensFoundCard {
  background: var(--bslr-warning);
  padding: 16px 24px;
  border-radius: 8px;
}
#webchat #copilot_AdaptiveNoLensFoundCard .ac-textBlock p {
  color: #fff !important;
  font-size: var(--base-font-size);
}
#webchat #copilot_AdaptiveNoLensFoundCard .ac-textBlock a {
  color: #fff !important;
}
#webchat #copilot_AdaptiveNoLensFoundCard .ac-textBlock a {
  color: #fff !important;
}
#webchat #copilot_AdaptiveNoLensFoundCard .ac-textBlock a:hover {
  text-decoration: none;
}
#webchat #copilot_AdaptiveNoLensFoundCard .ac-textBlock a img {
  display: none;
}
</style>
